import type { RequestUser, ServerConfig } from 'shared-types';

type ServerConfigParams = Pick<
  ServerConfig,
  'allowAdminToManageDeleteRequests' | 'disableAccountDeleteRequests'
>;

/**
 * Determines if account delete requests should be visible to the given `user`
 * based on the user's role, permissions, and server configuration.
 */
const shouldShowAccountDeleteRequests = ({
  accountDeleteRequests,
  serverConfig,
  user
}: {
  accountDeleteRequests: number;
  serverConfig: ServerConfigParams;
  user?: RequestUser;
}): boolean => {
  // Check user roles
  const userIsAdmin = user?.roles.includes('admin');
  const userIsSuperadmin = user?.roles.includes('superadmin');
  const userIsVuoadmin = user?.roles.includes('vuoadmin');

  // Check if there are any delete requests and that the feature is enabled
  const hasDeleteRequests =
    !serverConfig.disableAccountDeleteRequests && accountDeleteRequests > 0;

  // Check if the user has permission to handle delete requests
  const userCanActOnDeleteRequests =
    userIsSuperadmin ||
    (userIsAdmin &&
      user?.permissions.includes('manage-users') &&
      serverConfig.allowAdminToManageDeleteRequests);

  // Vuoadmins can always see requests, others need both permissions and active requests
  return userIsVuoadmin || (hasDeleteRequests && userCanActOnDeleteRequests);
};

export default shouldShowAccountDeleteRequests;
